import { useEffect, useRef, useState } from 'react';
import { useRevalidator } from 'react-router';
import { useAppFetcher } from '~/hooks/use-app-fetcher';
import { cn } from '@rocco/utils/cn';
import { useDeviceBrowserContext } from '@archipro-design/aria';
import { useTracker } from '@archipro-website/tracker';
import { usePublicUser } from '~/modules/user/hook/use-public-user';
import { useSocialLogins } from '~/modules/user/component/social-login/hook/use-social-logins';
import { useUserLoggedIn } from '~/modules/user/hook/use-user';
import { trackAuthEvent } from '~/modules/user/util/track-auth-event';
import {
    hdeSignUpValidator,
    loginValidator,
} from '~/modules/user/hook/use-login-flow';
import {
    assertErrorResponse,
    assertSuccessResponse,
} from '~/modules/root/graphql/responses';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Step7AccountProps {
    email: string;
    onEmailChange: (next: string) => void;
    onContinue: () => void;
    onBack?: () => void;
    submitting?: boolean;
    errorMessage?: string | null;
    /** Snapshot the brief to sessionStorage before a social-login redirect. */
    onPersistBeforeAuth?: () => void;
    /** Fired once when the in-modal sign-up creates an account. */
    onJustRegistered?: () => void;
    justRegistered?: boolean;
    /** Logged-in confirmation copy is rendered as `{prefix}<strong>{email}</strong>{suffix}`. */
    loggedInConfirmationPrefix: string;
    loggedInConfirmationSuffix: string;
    /** Body copy shown to anonymous users before the sign-up/log-in form. */
    anonymousBodyCopy: string;
}

export const Step7Account = ({
    email,
    onEmailChange,
    onContinue,
    onBack,
    submitting,
    errorMessage,
    onPersistBeforeAuth,
    onJustRegistered,
    justRegistered,
    loggedInConfirmationPrefix,
    loggedInConfirmationSuffix,
    anonymousBodyCopy,
}: Step7AccountProps) => {
    const userLogin = useUserLoggedIn();

    if (userLogin.isLoggedIn) {
        const firstName = userLogin.user.FirstName ?? '';
        const accountEmail = userLogin.user.Email ?? '';
        return (
            <ConfirmSubmitView
                heading={
                    firstName
                        ? `You're logged in as ${firstName}`
                        : `You're logged in`
                }
                accountEmail={accountEmail}
                confirmationPrefix={loggedInConfirmationPrefix}
                confirmationSuffix={loggedInConfirmationSuffix}
                onContinue={onContinue}
                onBack={onBack}
                submitting={submitting}
                errorMessage={errorMessage}
                successBanner={
                    justRegistered
                        ? `Account created${firstName ? `, welcome ${firstName}` : ''} — you're now logged in.`
                        : null
                }
            />
        );
    }

    return (
        <AnonymousStep7
            email={email}
            onEmailChange={onEmailChange}
            onContinue={onContinue}
            onBack={onBack}
            submitting={submitting}
            errorMessage={errorMessage}
            onPersistBeforeAuth={onPersistBeforeAuth}
            onJustRegistered={onJustRegistered}
            anonymousBodyCopy={anonymousBodyCopy}
        />
    );
};

interface ConfirmSubmitViewProps {
    heading: string;
    accountEmail: string;
    confirmationPrefix: string;
    confirmationSuffix: string;
    onContinue: () => void;
    onBack?: () => void;
    submitting?: boolean;
    errorMessage?: string | null;
    successBanner?: string | null;
}

const ConfirmSubmitView = ({
    heading,
    accountEmail,
    confirmationPrefix,
    confirmationSuffix,
    onContinue,
    onBack,
    submitting,
    errorMessage,
    successBanner,
}: ConfirmSubmitViewProps) => (
    <div className="flex flex-col gap-6">
        {successBanner && (
            <div
                role="status"
                className="flex items-start gap-2 rounded-[0.5rem] border border-[#cfe9d4] bg-[#eaf6ec] px-3 py-2 text-[0.8125rem] leading-[1.25] tracking-[-0.01em] text-[#1f5d2c]"
            >
                <CheckIcon />
                <span>{successBanner}</span>
            </div>
        )}
        <h2 className="text-[1.3125rem] leading-[1.1] font-medium tracking-[-0.02em] text-[#161616] uppercase">
            {heading}
        </h2>
        <p className="text-[0.875rem] leading-[1.3] tracking-[-0.02em] text-black">
            {confirmationPrefix}
            <strong>{accountEmail}</strong>
            {confirmationSuffix}
        </p>
        {errorMessage && (
            <span className="text-[0.75rem] text-red-600">{errorMessage}</span>
        )}
        <BackContinueRow
            onBack={onBack}
            onContinue={onContinue}
            submitting={submitting}
            continueLabel={submitting ? 'Sending…' : 'Submit brief'}
        />
    </div>
);

interface FieldErrors {
    email?: string;
    password?: string;
    fullName?: string;
    phone?: string;
}

type AnonymousStep7Props = Omit<
    Step7AccountProps,
    | 'justRegistered'
    | 'loggedInConfirmationPrefix'
    | 'loggedInConfirmationSuffix'
>;

const AnonymousStep7 = ({
    email,
    onEmailChange,
    onContinue,
    onBack,
    submitting,
    errorMessage,
    onPersistBeforeAuth,
    onJustRegistered,
    anonymousBodyCopy,
}: AnonymousStep7Props) => {
    const trimmedEmail = email.trim();
    const isEmailValid = EMAIL_PATTERN.test(trimmedEmail);

    const { publicUser, setPublicUser, handleUserExists } =
        usePublicUser('getMatched');
    const { isInstagramOrFacebookApp } = useDeviceBrowserContext();
    const tracker = useTracker();
    const revalidator = useRevalidator();
    const redirect =
        typeof window !== 'undefined' ? window.location.href : undefined;
    const availableSocials = useSocialLogins({
        loginOptions: publicUser?.socialLoginOptions,
        authSource: 'getMatched',
        redirect,
        skipPostSignUp: true,
        isInstagramOrFacebookApp,
    });

    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [password, setPassword] = useState('');
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [fullName, setFullName] = useState('');
    const [phone, setPhone] = useState('');
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [showEmailError, setShowEmailError] = useState(false);

    const signUpFetcher = useAppFetcher<Record<string, unknown>>();
    const isSigningUp = signUpFetcher.state !== 'idle';
    const signUpSuccess =
        signUpFetcher.data && assertSuccessResponse(signUpFetcher.data);
    const signUpServerError =
        (signUpFetcher.data &&
            assertErrorResponse(signUpFetcher.data) &&
            signUpFetcher.data.message) ||
        undefined;

    const loginFetcher = useAppFetcher<Record<string, unknown>>();
    const isLoggingIn = loginFetcher.state !== 'idle';
    const loginSuccess =
        loginFetcher.data && assertSuccessResponse(loginFetcher.data);
    const loginServerError =
        (loginFetcher.data &&
            assertErrorResponse(loginFetcher.data) &&
            loginFetcher.data.message) ||
        undefined;

    // Revalidate (not reload) so the modal stays mounted and the brief survives.
    const postSignUpDoneRef = useRef(false);
    useEffect(() => {
        if (!signUpSuccess || postSignUpDoneRef.current) return;
        postSignUpDoneRef.current = true;
        trackAuthEvent({
            tracker,
            event: 'new_account_registered',
            authSource: 'getMatched',
            provider: 'EmailPassword',
        });
        onJustRegistered?.();
        revalidator.revalidate();
    }, [signUpSuccess, tracker, revalidator, onJustRegistered]);

    const postLoginDoneRef = useRef(false);
    useEffect(() => {
        if (!loginSuccess || postLoginDoneRef.current) return;
        postLoginDoneRef.current = true;
        trackAuthEvent({
            tracker,
            event: 'user_login',
            authSource: 'getMatched',
            provider: 'EmailPassword',
        });
        revalidator.revalidate();
    }, [loginSuccess, tracker, revalidator]);

    const handleEmailBlur = () => {
        if (!trimmedEmail) return;
        // Mirror HDE's blur-time event so GA4 / archiproTracker see the same
        // funnel signal.
        trackAuthEvent({
            tracker,
            event: 'AuthEmailInput',
            authSource: 'getMatched',
            provider: 'EmailPassword',
        });
        if (isEmailValid) {
            void handleUserExists(trimmedEmail);
        }
    };

    const handleEmailChange = (next: string) => {
        onEmailChange(next);
        if (showEmailError) setShowEmailError(false);
        // If the user changes email after a recognition, drop the cached
        // public-user so the branch can flip back to the initial state and
        // trigger a fresh lookup on the next blur.
        if (publicUser && publicUser.email !== next.trim()) {
            setPublicUser(undefined);
        }
        if (fieldErrors.email) {
            setFieldErrors((prev) => ({ ...prev, email: undefined }));
        }
    };

    const isExisting = publicUser?.type === 'existing';
    const isNew = publicUser?.type === 'new';

    // Hold disabled past `isSigningUp`/`isLoggingIn` until the revalidator
    // flips `isLoggedIn`; otherwise a fast double-click can re-submit.
    const submitDisabled =
        submitting ||
        isSigningUp ||
        isLoggingIn ||
        signUpSuccess === true ||
        loginSuccess === true;
    const blockingError = errorMessage ?? signUpServerError ?? loginServerError;

    const handleContinue = () => {
        if (submitDisabled) return;
        if (!isEmailValid) {
            setShowEmailError(true);
            return;
        }
        if (!publicUser) {
            // Lookup hasn't returned yet — fire it and wait for the branch to flip.
            void handleUserExists(trimmedEmail);
            return;
        }
        if (isExisting) {
            const parsed = loginValidator.safeParse({
                email: trimmedEmail,
                password,
                rememberMe: 'true',
            });
            if (!parsed.success) {
                const flat = parsed.error.flatten().fieldErrors;
                setFieldErrors({
                    email: flat.email?.[0],
                    password: flat.password?.[0],
                });
                return;
            }
            setFieldErrors({});

            tracker.log('accountLogin', {
                url: new URL(window.location.href),
                data: { step: 2, signInMethod: 'Email' },
            });
            trackAuthEvent({
                tracker,
                event: 'AuthSignInSubmit',
                authSource: 'getMatched',
                provider: 'EmailPassword',
            });
            loginFetcher.submit(
                { email: trimmedEmail, password, rememberMe: 'true' },
                { method: 'post', action: '/remix-api/user/login' },
            );
            return;
        }
        if (!isNew) return;
        // New user: validate registration fields then submit sign-up.
        const parts = fullName.trim().split(/\s+/).filter(Boolean);
        const firstName = parts[0] ?? '';
        const lastName = parts.slice(1).join(' ');
        const parsed = hdeSignUpValidator.safeParse({
            email: trimmedEmail,
            password,
            firstName,
            lastName,
            phone: phone.trim() || undefined,
            rememberMe: 'true',
        });
        if (!parsed.success) {
            const flat = parsed.error.flatten().fieldErrors;
            setFieldErrors({
                email: flat.email?.[0],
                password: flat.password?.[0],
                fullName: flat.firstName?.[0] ?? flat.lastName?.[0],
                phone: flat.phone?.[0],
            });
            return;
        }
        setFieldErrors({});

        trackAuthEvent({
            tracker,
            event: 'AuthSignUpSubmit',
            authSource: 'getMatched',
            provider: 'EmailPassword',
        });

        signUpFetcher.submit(
            {
                email: trimmedEmail,
                password,
                firstName,
                lastName,
                phone: phone.trim(),
                rememberMe: 'true',
            },
            { method: 'post', action: '/remix-api/user/sign-up' },
        );
    };

    const emailDisplayError =
        fieldErrors.email ??
        (showEmailError && !isEmailValid
            ? 'Please enter a valid email.'
            : null);

    const heading = isExisting
        ? publicUser?.firstName
            ? `Welcome back, ${publicUser.firstName}`
            : `Welcome back`
        : 'Create your free archipro account or log in';
    const body = isExisting
        ? "Enter your password to sign in and we'll send your matched results to your account email."
        : anonymousBodyCopy;

    const onPasswordChange = (v: string) => {
        setPassword(v);
        if (fieldErrors.password) {
            setFieldErrors((p) => ({ ...p, password: undefined }));
        }
    };

    if (showForgotPassword) {
        return (
            <ForgotPasswordPanel
                email={email}
                onBack={() => setShowForgotPassword(false)}
            />
        );
    }

    return (
        <div className="flex min-h-full flex-col gap-6">
            <h2 className="text-[1.3125rem] leading-[1.1] font-medium tracking-[-0.02em] text-[#161616] uppercase">
                {heading}
            </h2>
            <p className="text-[0.875rem] leading-[1.3] tracking-[-0.02em] text-black">
                {body}
            </p>

            <Field label="Your Email" error={emailDisplayError}>
                <input
                    type="email"
                    value={email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    onBlur={handleEmailBlur}
                    placeholder="your@email.com"
                    disabled={submitDisabled}
                    autoComplete="email"
                    className={cn(inputClass, emailDisplayError && errorRing)}
                />
            </Field>

            {isExisting && (
                <>
                    <PasswordField
                        value={password}
                        onChange={onPasswordChange}
                        visible={passwordVisible}
                        onVisibilityToggle={() => setPasswordVisible((v) => !v)}
                        error={fieldErrors.password}
                        disabled={submitDisabled}
                    />
                    <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="self-center text-[0.78rem] text-[#858585] underline hover:text-black"
                    >
                        Forgot password?
                    </button>
                </>
            )}

            {isNew && (
                <RegisterFields
                    password={password}
                    onPasswordChange={onPasswordChange}
                    passwordVisible={passwordVisible}
                    onPasswordVisibilityToggle={() =>
                        setPasswordVisible((v) => !v)
                    }
                    fullName={fullName}
                    onFullNameChange={(v) => {
                        setFullName(v);
                        if (fieldErrors.fullName) {
                            setFieldErrors((p) => ({
                                ...p,
                                fullName: undefined,
                            }));
                        }
                    }}
                    phone={phone}
                    onPhoneChange={(v) => {
                        setPhone(v);
                        if (fieldErrors.phone) {
                            setFieldErrors((p) => ({
                                ...p,
                                phone: undefined,
                            }));
                        }
                    }}
                    errors={fieldErrors}
                    disabled={submitDisabled}
                />
            )}

            {blockingError && (
                <span className="text-[0.75rem] text-red-600">
                    {blockingError}
                </span>
            )}

            <BackContinueRow
                onBack={onBack}
                onContinue={handleContinue}
                submitting={submitDisabled}
                continueLabel={
                    isSigningUp || signUpSuccess
                        ? 'Creating account…'
                        : isLoggingIn || loginSuccess
                          ? 'Signing in…'
                          : submitting
                            ? 'Sending…'
                            : 'Continue'
                }
            />

            {availableSocials.length > 0 && (
                <div className="mt-auto flex flex-col items-center gap-3">
                    <span className="text-[0.75rem] tracking-[-0.02em] text-[#858585]">
                        Or continue with:
                    </span>
                    <div className="flex flex-wrap justify-center gap-2">
                        {availableSocials.map((social) => (
                            <a
                                key={social.KEY}
                                href={`/member/${social.URL}`}
                                data-button={social.TRACKER_ID}
                                onClick={() => {
                                    onPersistBeforeAuth?.();
                                    tracker.log('accountLogin', {
                                        url: new URL(window.location.href),
                                        data: {
                                            step: 2,
                                            signInMethod: social.TITLE,
                                        },
                                    });
                                    trackAuthEvent({
                                        tracker,
                                        event: 'AuthSocialButtonClick',
                                        authSource: 'getMatched',
                                        provider: social.TITLE,
                                    });
                                }}
                                className="inline-flex h-11 items-center gap-2 rounded-full border border-[#dcdcdc] bg-white px-5 text-[0.875rem] font-medium tracking-[-0.01em] text-[#161616] transition-colors hover:bg-[#f5f5f5]"
                            >
                                <span
                                    aria-hidden
                                    className="mr-1 inline-flex size-[0.9rem] items-center justify-center [&>svg]:size-full"
                                >
                                    {social.ICON}
                                </span>
                                {social.TITLE}
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

interface ForgotPasswordPanelProps {
    email: string;
    onBack: () => void;
}

const ForgotPasswordPanel = ({ email, onBack }: ForgotPasswordPanelProps) => {
    const [fpEmail, setFpEmail] = useState(email);
    const fetcher = useAppFetcher<Record<string, unknown>>();
    const sent = fetcher.data != null && 'success' in fetcher.data;
    const isBusy = fetcher.state !== 'idle';

    const handleSend = () => {
        if (isBusy || !fpEmail.trim()) return;
        fetcher.submit(
            { email: fpEmail.trim() },
            { method: 'post', action: '/remix-api/user/forgot-password' },
        );
    };

    if (sent) {
        return (
            <div className="flex flex-col gap-6">
                <h2 className="text-[1.3125rem] leading-[1.1] font-medium tracking-[-0.02em] text-[#161616] uppercase">
                    Check your email
                </h2>
                <p className="text-[0.875rem] leading-[1.3] tracking-[-0.02em] text-black">
                    We sent an email to <strong>{fpEmail}</strong>. If this
                    email is connected to an ArchiPro account, you&apos;ll be
                    able to reset your password.
                </p>
                <p className="text-[0.875rem] leading-[1.3] tracking-[-0.02em] text-black">
                    Didn&apos;t get it?{' '}
                    <button
                        type="button"
                        onClick={handleSend}
                        disabled={isBusy}
                        className="underline hover:text-[#858585] disabled:opacity-60"
                    >
                        Resend email
                    </button>
                </p>
                <div className="flex justify-center pt-2">
                    <button
                        type="button"
                        onClick={onBack}
                        className="inline-flex h-[2.85rem] items-center justify-center rounded-full border border-black bg-transparent px-7 text-[1rem] font-medium tracking-[0.02em] text-[#1c1d1f] transition-colors hover:bg-black hover:text-white"
                    >
                        Back to sign in
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <h2 className="text-[1.3125rem] leading-[1.1] font-medium tracking-[-0.02em] text-[#161616] uppercase">
                Forgot password?
            </h2>
            <p className="text-[0.875rem] leading-[1.3] tracking-[-0.02em] text-black">
                Enter the email address associated with your account and
                we&apos;ll email you a link to reset your password.
            </p>
            <Field label="Email address">
                <input
                    type="email"
                    value={fpEmail}
                    onChange={(e) => setFpEmail(e.target.value)}
                    placeholder="your@email.com"
                    disabled={isBusy}
                    autoComplete="email"
                    className={inputClass}
                />
            </Field>
            <div className="flex justify-center gap-3 pt-2">
                <button
                    type="button"
                    onClick={onBack}
                    disabled={isBusy}
                    className="inline-flex h-[2.85rem] items-center justify-center rounded-full border border-black bg-transparent px-7 text-[1rem] font-medium tracking-[0.02em] text-[#1c1d1f] transition-colors hover:bg-black hover:text-white disabled:opacity-60"
                >
                    Back
                </button>
                <PrimaryButton
                    onClick={handleSend}
                    disabled={isBusy}
                    loading={isBusy}
                >
                    Send reset link
                </PrimaryButton>
            </div>
        </div>
    );
};

interface RegisterFieldsProps {
    password: string;
    onPasswordChange: (next: string) => void;
    passwordVisible: boolean;
    onPasswordVisibilityToggle: () => void;
    fullName: string;
    onFullNameChange: (next: string) => void;
    phone: string;
    onPhoneChange: (next: string) => void;
    errors: FieldErrors;
    disabled?: boolean;
}

const RegisterFields = ({
    password,
    onPasswordChange,
    passwordVisible,
    onPasswordVisibilityToggle,
    fullName,
    onFullNameChange,
    phone,
    onPhoneChange,
    errors,
    disabled,
}: RegisterFieldsProps) => (
    <>
        <PasswordField
            value={password}
            onChange={onPasswordChange}
            visible={passwordVisible}
            onVisibilityToggle={onPasswordVisibilityToggle}
            error={errors.password}
            disabled={disabled}
            autoComplete="new-password"
        />
        <Field label="Full Name" error={errors.fullName}>
            <input
                type="text"
                value={fullName}
                onChange={(e) => onFullNameChange(e.target.value)}
                placeholder="Enter your full name"
                disabled={disabled}
                autoComplete="name"
                className={cn(inputClass, errors.fullName && errorRing)}
            />
        </Field>
        <Field label="Phone Number (Optional)" error={errors.phone}>
            <input
                type="tel"
                value={phone}
                onChange={(e) => onPhoneChange(e.target.value)}
                placeholder="Enter your phone number"
                disabled={disabled}
                autoComplete="tel"
                className={cn(inputClass, errors.phone && errorRing)}
            />
        </Field>
    </>
);

interface PasswordFieldProps {
    value: string;
    onChange: (next: string) => void;
    visible: boolean;
    onVisibilityToggle: () => void;
    error?: string;
    disabled?: boolean;
    autoComplete?: 'new-password' | 'current-password';
}

const PasswordField = ({
    value,
    onChange,
    visible,
    onVisibilityToggle,
    error,
    disabled,
    autoComplete = 'current-password',
}: PasswordFieldProps) => (
    <Field label="Password" error={error}>
        <div className="relative">
            <input
                type={visible ? 'text' : 'password'}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Enter password"
                disabled={disabled}
                autoComplete={autoComplete}
                className={cn(inputClass, 'pr-12', error && errorRing)}
            />
            <button
                type="button"
                onClick={onVisibilityToggle}
                aria-label={visible ? 'Hide password' : 'Show password'}
                className="absolute inset-y-0 right-3 flex items-center text-[#858585] hover:text-black"
            >
                {visible ? <EyeOffIcon /> : <EyeIcon />}
            </button>
        </div>
    </Field>
);

const inputClass =
    'w-full rounded-[0.75rem] bg-[#fdfdfd] px-3 py-4 text-[0.9rem] tracking-[-0.02em] text-black placeholder:text-black/30 focus:outline-none disabled:opacity-60';
const errorRing = 'ring-1 ring-red-500';

interface FieldProps {
    label: string;
    error?: string | null;
    children: React.ReactNode;
}

const Field = ({ label, error, children }: FieldProps) => (
    <div className="flex flex-col gap-2">
        <span className="text-[0.78rem] leading-[1.1] font-medium tracking-[-0.02em] text-[#161616] uppercase">
            {label}
        </span>
        {children}
        {error && <span className="text-[0.75rem] text-red-600">{error}</span>}
    </div>
);

interface BackContinueRowProps {
    onBack?: () => void;
    onContinue: () => void;
    submitting?: boolean;
    continueLabel: React.ReactNode;
}

const BackContinueRow = ({
    onBack,
    onContinue,
    submitting,
    continueLabel,
}: BackContinueRowProps) => (
    <div className="flex justify-center gap-3 pt-2">
        {onBack && (
            <button
                type="button"
                onClick={onBack}
                disabled={submitting}
                className="inline-flex h-[2.85rem] items-center justify-center rounded-full border border-black bg-transparent px-7 text-[1rem] font-medium tracking-[0.02em] text-[#1c1d1f] transition-colors hover:bg-black hover:text-white disabled:opacity-60"
            >
                Back
            </button>
        )}
        <PrimaryButton
            onClick={onContinue}
            disabled={submitting}
            loading={submitting}
        >
            {continueLabel}
        </PrimaryButton>
    </div>
);

interface PrimaryButtonProps {
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
    children: React.ReactNode;
}

const PrimaryButton = ({
    onClick,
    disabled,
    loading,
    children,
}: PrimaryButtonProps) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="inline-flex h-[2.85rem] items-center justify-center gap-2 rounded-full bg-[#323232] px-9 text-[1rem] font-medium tracking-[0.02em] text-white shadow-md transition-colors hover:bg-black disabled:opacity-60"
    >
        {loading && (
            <span
                aria-hidden
                className="size-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
            />
        )}
        {children}
    </button>
);

const EyeIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const CheckIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className="mt-[0.125rem] shrink-0"
    >
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const EyeOffIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);
